import React, { useState } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import { Spinner, ScrollView, IconChevronRight } from '../../../Common'
import styled, { css } from 'styled-components'
import { Config } from '../../../../config'
pdfjs.GlobalWorkerOptions.workerSrc = `https://storage.googleapis.com/production.hippoland-contents.com/pdf/pdf-worker.js`

const FADE_ANIMATION_TIME = 600

const PDF = ({
  pdf,
  width,
  height,
  errorMessage,
}: {
  pdf: string
  width: number
  height: number
  errorMessage: string
}) => {
  const [numPages, setNumPages] = useState<number | null>(null)
  const [pageNumber, setPageNumber] = useState(1)
  const [fade, setFade] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isError, setIsError] = useState(false)

  function onLoad(data: { numPages: number; isSuccess: boolean }) {
    setNumPages(data.numPages)
    setIsError(!data.isSuccess)

    setIsLoading(false)
  }

  const triggerFade = (callback: () => void) => {
    setFade(true)

    setTimeout(() => {
      setFade(false)
      callback()
    }, FADE_ANIMATION_TIME)
  }

  const goToPrevPage = () => {
    triggerFade(() => setPageNumber(Math.max(1, pageNumber - 1)))
  }

  const goToNextPage = () => {
    if (!numPages) return
    triggerFade(() => setPageNumber(Math.min(numPages, pageNumber + 1)))
  }

  return (
    <Container style={{ width, minHeight: height }}>
      <div>
        <DocumentContainer>
          <ScrollView overflowDirection="x">
            <Document
              loading={() => <></>}
              error={() => <></>}
              file={pdf}
              onLoadSuccess={(data) =>
                onLoad({ numPages: data.numPages, isSuccess: true })
              }
              onLoadError={() => onLoad({ numPages: 1, isSuccess: false })}
              renderMode="canvas"
            >
              <PageContainer fade={fade} style={{ width, height }}>
                <Page width={width} height={height} pageNumber={pageNumber} />
              </PageContainer>
            </Document>
          </ScrollView>
        </DocumentContainer>
        {!isLoading && !isError && (
          <>
            <Pagination>
              {pageNumber} / {numPages}
            </Pagination>
            <PrevButton onClick={goToPrevPage} isDisabled={pageNumber === 1}>
              <IconChevronRight />
            </PrevButton>
            <NextButton
              onClick={goToNextPage}
              isDisabled={pageNumber === numPages}
            >
              <IconChevronRight />
            </NextButton>
          </>
        )}
        {isLoading && (
          <SpinnerContainer>
            <Spinner />
          </SpinnerContainer>
        )}

        {isError && <ErrorMessage>{errorMessage}</ErrorMessage>}
      </div>
    </Container>
  )
}

export default PDF

const Container = styled.div.attrs({
  className: 'd-flex flex-column justify-content-end',
})`
  position: relative;
`

const DocumentContainer = styled.div`
  width: 100%;
  position: relative;

  & > div {
    width: 100%;
  }
`

const ErrorMessage = styled.div.attrs({
  className: 'p-4',
})`
  text-align: center;
  border-radius: 0.4rem;
  font-size: 2.4rem;
  position: absolute;
  color: ${({ theme }) => theme.palette.danger};
  left: 50%;
  bottom: 50%;
  transform: translate(-50%, 50%);
  z-index: 200;
`

const PageContainer = styled.div<{ fade?: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  opacity: ${(props) => (props.fade ? 0 : 1)};
  transition: opacity ${FADE_ANIMATION_TIME}ms cubic-bezier(0.4, 0, 0.2, 1);

  transform: translateY(-100%);
  z-index: 100;
  max-width: 100%;
  overflow-x: auto;
`

const SpinnerContainer = styled.div``

const PaginationButton = styled.button.attrs({
  className: 'p-0 d-flex align-items-center justify-content-center',
})<{ isDisabled?: boolean }>`
  min-width: 0 !important;
  min-height: 0 !important;
  width: 4.4rem;
  height: 4.4rem;
  z-index: 200;

  border-radius: 50%;
  background: ${({ theme }) => theme.palette.secondary};
  box-shadow: 0 0 0.8rem ${({ theme }) => theme.palette.lightgrey};

  transition: box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1);

  position: absolute;
  bottom: 50%;
  transform: translateY(50%);

  & svg {
    width: 1.4rem;
    opacity: 0.5;
  }

  &:hover {
    box-shadow: 0 0.5rem 1.6rem rgba(0, 0, 0, 0.15);
    opacity: 1 !important;
  }

  ${(props) =>
    props.isDisabled
      ? css`
          pointer-events: none;
          opacity: 0.6 !important;
        `
      : ''}
`

const PrevButton = styled(PaginationButton)`
  left: 4rem;
  transform: translateY(50%) rotate(180deg);

  @media (min-width: ${({ theme }) => theme.breakpoints.md.min}) {
    left: -6rem;
  }
`

const NextButton = styled(PaginationButton)`
  right: 4rem;

  @media (min-width: ${({ theme }) => theme.breakpoints.md.min}) {
    right: -6rem;
  }
`

const Pagination = styled.div.attrs({
  className: 'd-flex align-items-center justify-content-center p-4',
})`
  border-radius: 0.4rem;
  position: absolute;
  background: ${({ theme }) => theme.palette.secondary};
  width: 8rem;
  max-width: 8rem;
  left: 50%;
  bottom: 2rem;
  transform: translateX(-50%);
  z-index: 200;
`
